
import React from 'react';

interface YouTubeEmbedProps {
  embedUrl: string;
  title: string;
  className?: string;
}

const YouTubeEmbed: React.FC<YouTubeEmbedProps> = ({ embedUrl, title, className = '' }) => {
  // Convert any YouTube URL to the proper embed format
  const getEmbedUrl = (url: string): string => {
    // If url is empty or undefined, return empty string
    if (!url) return '';
    
    try {
      // Check if it's already an embed URL
      if (url.includes('youtube.com/embed/')) {
        return url;
      }
      
      // Extract video ID from various YouTube URL formats
      let videoId = '';
      
      // Standard youtube.com/watch?v=VIDEO_ID format
      if (url.includes('youtube.com/watch')) {
        const urlObj = new URL(url);
        videoId = urlObj.searchParams.get('v') || '';
      } 
      // Short youtu.be/VIDEO_ID format
      else if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1]?.split('?')[0] || '';
      }
      // iframe embed format (<iframe src="https://www.youtube.com/embed/VIDEO_ID">)
      else if (url.includes('<iframe')) {
        const match = url.match(/src=["']https:\/\/www\.youtube\.com\/embed\/([^?"']+)/);
        videoId = match ? match[1] : '';
      }
      // Handle direct video ID input (11 characters)
      else if (url.match(/^[a-zA-Z0-9_-]{11}$/)) {
        videoId = url;
      }
      
      // If we have a valid video ID, construct the embed URL
      if (videoId) {
        console.log(`Converting YouTube URL to embed with videoId: ${videoId}`);
        return `https://www.youtube.com/embed/${videoId}`;
      }
      
      // If we couldn't extract a video ID from a standard URL format,
      // Let's try one more approach - extract from the URL directly
      if (url.includes('youtube.com/')) {
        const segments = url.split('/');
        const lastSegment = segments[segments.length - 1];
        if (lastSegment && lastSegment.length === 11) {
          return `https://www.youtube.com/embed/${lastSegment}`;
        }
      }
      
      // If all else fails, return the original URL
      console.log(`Using original YouTube URL: ${url}`);
      return url;
    } catch (error) {
      console.error(`Error parsing YouTube URL: ${url}`, error);
      return url;
    }
  };

  const finalEmbedUrl = getEmbedUrl(embedUrl);
  
  if (!finalEmbedUrl) {
    return <div className={`aspect-video w-full ${className} bg-slate-100 flex items-center justify-center`}>
      <p className="text-muted-foreground">Video unavailable</p>
    </div>;
  }

  return (
    <div className={`aspect-video w-full ${className}`}>
      <iframe
        width="100%"
        height="100%"
        src={finalEmbedUrl}
        title={title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
        loading="lazy"
        className="rounded-md"
      />
    </div>
  );
};

export default YouTubeEmbed;
